/**
 * Auth adapter — re-exports from @codespeak11/cognito-passwordless
 * with backward-compatible API surface.
 *
 * Also registers `getValidToken` and `logout` with the token bridge so
 * non-React modules (axios interceptors) can call them. This mirrors the
 * pattern in bdanceinfra/docs/spa-auth-integration.md, which assumes every
 * authenticated request goes through `getValidToken()` for proactive refresh.
 */
import {
  AuthProvider as CpAuthProvider,
  useAuth as cpUseAuth,
} from '@codespeak11/cognito-passwordless';
import { useEffect, type ReactNode } from 'react';
import { registerAuthBridge } from './tokenBridge';

interface User {
  sub: string;
  email: string;
}

interface AuthContextValue {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  initiateAuth: (email: string) => Promise<void>;
  verifyOtp: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
}

function AuthBridgeBinder({ children }: { children: ReactNode }) {
  const auth = cpUseAuth();
  useEffect(() => {
    registerAuthBridge({
      getValidToken: auth.getValidToken,
      logout: auth.logout,
    });
  }, [auth.getValidToken, auth.logout]);
  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <CpAuthProvider>
      <AuthBridgeBinder>{children}</AuthBridgeBinder>
    </CpAuthProvider>
  );
}

export function useAuth(): AuthContextValue {
  const auth = cpUseAuth();
  return {
    user: auth.user ? { sub: auth.user.sub, email: auth.user.email } : null,
    accessToken: auth.accessToken,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    initiateAuth: async () => { /* no-op — SSO handles auth via portal redirect */ },
    verifyOtp: async () => { /* no-op — SSO handles verification */ },
    logout: auth.logout,
    getValidToken: auth.getValidToken,
  };
}
